import 'mapbox-gl/dist/mapbox-gl.css';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Map as MapGL, type MapRef } from 'react-map-gl/mapbox';
import { useFocusMap } from '../../../hooks/useFocusMap';
import { useGcsStore } from '../../../store/useGcsStore';
import { useSelectedGcsConfigsStore } from '../../../store/useSelectedGcsConfigsStore';
import { useFollowedVehicleStore } from '../../../store/useFollowedVehicleStore';
import { selectFollowedVehicleData } from '../../../store/selectors/selectFollowedFlightData';
import { GcsControlZone } from './GcsControlZone';
import { GcsMissions } from './GcsMissions';
import { GcsForeignAircrafts } from './GcsForeignAircrafts';
import { useFollowedVehicleMapEffects } from './useFollowedVehicleMapEffects';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
const USE_MAPBOX_TEST_MODE = import.meta.env.VITE_MAPBOX_TEST_MODE === 'true';

const FlightMap = forwardRef<unknown>((_props, ref) => {
  const mapRef = useRef<MapRef | null>(null);
  const focusMap = useFocusMap(mapRef);
  const { selectedConfigs } = useSelectedGcsConfigsStore();
  const followedVehicle = useFollowedVehicleStore((state) => state.followedVehicle);

  const followedVehicleData = useGcsStore((state) => selectFollowedVehicleData(state.data, followedVehicle));

  useFollowedVehicleMapEffects({
    mapRef,
    followedVehicleData
  });

  useImperativeHandle(
    ref,
    () => ({
      fitBounds: (...args: Parameters<MapRef['fitBounds']>) => mapRef.current?.fitBounds(...args),
      focusMap
    }),
    [focusMap]
  );

  return (
    <MapGL
      ref={mapRef}
      initialViewState={{
        longitude: -122.4,
        latitude: 37.8,
        zoom: 10
      }}
      mapStyle='mapbox://styles/mapbox/satellite-streets-v12'
      mapboxAccessToken={USE_MAPBOX_TEST_MODE ? undefined : MAPBOX_TOKEN}
      testMode={USE_MAPBOX_TEST_MODE}
      logoPosition='bottom-right'
    >
      {/* GCS-specific control zone */}
      {selectedConfigs.map((config) => (
        <GcsControlZone key={`control-zone-${config.id}`} gcsId={config.id} />
      ))}

      {/* GCS-specific missions */}
      {selectedConfigs.map((config) => (
        <GcsMissions key={`missions-${config.id}`} gcsId={config.id} />
      ))}

      {/* GCS-specific foreign aircrafts */}
      {selectedConfigs.map((config) => (
        <GcsForeignAircrafts key={`foreign-aircraft-${config.id}`} gcsId={config.id} />
      ))}
    </MapGL>
  );
});

export default FlightMap;
